import React from 'react';

function formatDateTime(value) {
  if (!value) return '—';

  try {
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return value;
  }
}

function formatStatus(value) {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function LimitBar({ planData, emailsCount, C, style }) {
  if (!planData || !planData.features) return null;
  const maxEmailsFeat = planData.features.find((feature) => feature.code.includes('emails') || feature.code.includes('limit') || feature.code.includes('max'));
  if (!maxEmailsFeat) return null;

  const max = parseInt(maxEmailsFeat.value, 10) || 100;
  const usage = Math.min(emailsCount, max);
  const pct = Math.round((usage / max) * 100);

  return (
    <div style={{ ...style }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.muted, marginBottom: 6 }}>
        <span>Inbox Usage limit</span>
        <span><strong style={{ color: C.text }}>{emailsCount}</strong> / {max}</span>
      </div>
      <div style={{ width: '100%', height: 4, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct >= 90 ? C.danger : 'linear-gradient(90deg, #6366f1, #8b5cf6)', borderRadius: 2, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

export default function MyAccountPage({ accountInfo, accountLoading, accountErr, planData, err, emailsCount, C, STYLES }) {
  const detailRows = [
    ['Full Name', accountInfo?.full_name || '—'],
    ['Organization', accountInfo?.organization_name || '—'],
    ['Last Login', formatDateTime(accountInfo?.last_login)],
    ['Created At', formatDateTime(accountInfo?.created_at)],
    ['Updated At', formatDateTime(accountInfo?.updated_at)],
  ];

  return (
    <div style={STYLES.accountWrap}>
      <div style={STYLES.accountTitle}>🛡️ My Account</div>
      <div style={STYLES.accountSub}>View your account profile, activity, and current subscription plan.</div>

      {accountErr ? <div style={STYLES.errMsg}>{accountErr}</div> : null}

      {accountLoading && !accountInfo ? (
        <div style={{ ...STYLES.emptyText, marginTop: 20, marginBottom: 24 }}>Loading account information...</div>
      ) : accountInfo ? (
        <div style={{ marginTop: 20, marginBottom: 24, background: C.panel, padding: 24, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Account Name</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{accountInfo.full_name || accountInfo.username}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{accountInfo.organization_name || 'No organization assigned'}</div>
            </div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Account Status</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: accountInfo.status === 'active' ? C.green : C.text }}>{formatStatus(accountInfo.status)}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Last login {formatDateTime(accountInfo.last_login)}</div>
            </div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Inbox Usage</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{emailsCount}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Captured emails for this account</div>
            </div>
          </div>

          <table style={STYLES.table}>
            <thead>
              <tr>
                <th style={STYLES.th}>Field</th>
                <th style={STYLES.th}>Value</th>
              </tr>
            </thead>
            <tbody>
              {detailRows.map(([label, value]) => (
                <tr key={label} className="trow">
                  <td style={{ ...STYLES.td, fontWeight: 600, color: C.text }}>{label}</td>
                  <td style={STYLES.td}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {err ? (
        <div style={STYLES.errMsg}>{err}</div>
      ) : !planData ? (
        <div style={{ ...STYLES.emptyText, marginTop: 40 }}>Loading plan data...</div>
      ) : planData.plan === null ? (
        <div style={{ ...STYLES.emptyText, marginTop: 40 }}>No active subscription plan found.</div>
      ) : (
        <div style={{ marginTop: 20, background: C.panel, padding: 24, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <h3 style={{ margin: '0 0 16px 0', color: C.text, fontSize: 18 }}>
            Current Plan: <span style={{ color: C.accent }}>{planData.plan}</span>
          </h3>

          <LimitBar planData={planData} emailsCount={emailsCount} C={C} style={{ marginBottom: 24 }} />

          <table style={STYLES.table}>
            <thead>
              <tr>
                <th style={STYLES.th}>Feature</th>
                <th style={STYLES.th}>Value</th>
              </tr>
            </thead>
            <tbody>
              {planData.features.map((feature, index) => (
                <tr key={index} className="trow">
                  <td style={{ ...STYLES.td, textTransform: 'capitalize' }}>{feature.code.replace(/_/g, ' ')}</td>
                  <td style={{ ...STYLES.td, fontWeight: 'bold', color: '#a78bfa' }}>{feature.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}